import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import { Tabs } from '@mui/material';
import Tab from '@mui/material/Tab';
import { orderBy } from '@progress/kendo-data-query';
import {
    getActivePanels,
    getDashboardPanels,
} from '@components/central-monitoring/manage-observation/manage-observation.helper';
import { SiteDropdown } from '@pages/central-monitoring/site-dropdown';
import ControlObservation from '../../components/central-monitoring/control-observation';
import { useLoading } from '@contexts-clean/loader-context';
import {
    getSessionStorageActivePageTabLegacy,
    setSessionStorageActivePageTabLegacy,
} from '@utils-clean/page-tab-helpers';
import { Permissions } from 'contexts/user-context';
import { useAuthorization, useBoolean, useLocalState, useTranslation } from 'hooks';
import { GET_CM_REVIEW_REPORT_DATA, GET_DATASET_REVIEWS, GET_DATASETS, GET_PANELS } from 'queries/central-monitoring';
import { GET_RESTRICTED_STUDY, GET_STUDY_MEMBERS } from 'queries/studies';
import { DashboardType } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import NotificationService from 'utils/notification';
import AddAction from 'views/components/central-monitoring/add-action';
import CompanionView from 'views/components/central-monitoring/companion-view';
import DashboardPanelList from 'views/components/central-monitoring/dashboard-panel-list';
import EditReview from 'views/components/central-monitoring/edit-review';
import ManageObservation from 'views/components/central-monitoring/manage-observation/manage-observation';
import AddPanelDialog from 'views/components/central-monitoring/panels/add-panel';
import SiteDashboardSummaryV2 from 'views/components/central-monitoring/site-dashboard-summary-v2';
import StudyDashboardSummary from 'views/components/central-monitoring/study-summary';
import Loading from 'views/components/core/loading';
import { SelectWithSearchDataArrayInterface } from 'views/components/core/select-with-categories/select-with-categories.interface';
import Error from 'views/components/error/error';
import StudyDatasetNotes from 'views/pages/admin/study/admin-study-dataset-notes';
import 'views/pages/central-monitoring/legacy-dashboards.scss';
import { getDatasetDates, getObservationTypeFromDashboard } from '@utils/cm-observations.service';
import { notEmpty } from '@utils/utility-helpers';
import {
    dataSets,
    dataSetsVariables,
    dataSetReviews,
    dataSetReviewsVariables,
} from '@queries/generated/central-monitoring.generated';
import {
    SiteFields,
    StudyFields_Study_members_StudyMember,
    PanelForObservationFields,
    DataSetFields,
    DashboardFields,
    SiteReviewFields,
    PanelFields,
    CentralMonitoringObservationAndActionFields,
    CentralMonitoringActionFields,
} from '@queries/generated/fragments.generated';
import { getSite, getSiteVariables } from '@queries/generated/sites.generated';
import {
    getRestrictedStudy,
    getRestrictedStudy_OpraQueries_study_Study,
    getStudyMembers,
} from '@queries/generated/studies.generated';
import { GET_SITE } from '@queries/sites';

interface DashboardsInterface {
    type: DashboardType.STUDY | DashboardType.SITE;
}

export interface DashboardSite extends Pick<SiteFields, 'id' | 'name'> {
    status?: string | null;
    default: boolean;
    favourite: boolean;
}

interface DashboardLocationState {
    skipSelectDate?: boolean;
    showDashboard?: boolean;
    siteId?: string | null;
}

const LegacyDashboards = ({ type }: DashboardsInterface) => {
    const { t } = useTranslation();
    const user = useAuthorization();
    const { isLoading: isLoadingContext } = useLoading();
    const location = useLocation();
    const navigate = useNavigate();

    const [locationState] = useState<DashboardLocationState | null>((location.state as DashboardLocationState) || null);

    // Despite the clearing of location state below,
    // when the user navigates from study to site dashboard pages via "View Site Dashboard" option,
    // the location state is updated but the component level locationState is now null, so still need to fall back to location.state
    // the navigate useEffect still runs after this render so it still gets cleared as expected eventually
    const siteIdFromLocationState = locationState?.siteId ?? location.state?.siteId;
    const showDashboard = locationState?.showDashboard ?? location.state?.showDashboard;

    // This ensures location state is cleared once it has been assigned to the component state above
    // This avoids values persisting that are intended only for the first time this page is loaded following a call to navigate
    // namely, those to do with "View Site Dashboard", which are no longer relevant if user refreshes etc
    useEffect(() => {
        navigate('.', { replace: true });
    }, [navigate]);

    const canView = user?.canRead?.({
        module: Constants.Modules.CM,
        element: Constants.DataElements.CentralMonitoring,
        permission: Permissions.Read,
    });
    const canEdit = user?.canWrite?.({
        module: Constants.Modules.CM,
        element: Constants.DataElements.CentralMonitoring,
        permission: Permissions.ReadWrite,
    });

    const canSavePanelSetup =
        user?.canWriteSystemAdminLevel?.() && user.systemAdminLevel !== Constants.SystemAdminLevel.Contributor;

    const { state } = useLocalState();

    const [active, setActive] = useState(getSessionStorageActivePageTabLegacy());
    const [activeTab, setActiveTab] = useState(0);
    const handleTabChange = (tab: number) => {
        setActive(tab);
        setSessionStorageActivePageTabLegacy(tab);
    };

    const [datasetDates, setDatasetDates] = useState<DataSetFields[] | null>(null);
    const [study, setStudy] = useState<getRestrictedStudy_OpraQueries_study_Study | null>(null);
    const [sites, setSites] = useState<DashboardSite[]>([]);
    const [dropdownSites, setDropdownSites] = useState<DashboardSite[]>([]);
    const [selectedSite, setSelectedSite] = useState<DashboardSite | null>(null);
    const [panelsData, setPanelsData] = useState<PanelForObservationFields[]>([]);
    const [currentAction, setCurrentAction] = useState<CentralMonitoringActionFields | null>(null);
    const [currentObservation, setCurrentObservation] = useState<CentralMonitoringObservationAndActionFields | null>(
        null,
    );
    const [currentDashboardsData, setCurrentDashboardsData] = useState<DashboardFields[] | null>(null);
    const [currentDataSets, setCurrentDataSets] = useState<{ dataSets: DataSetFields[] } | null>(null);
    const [currentDataSetsReview, setCurrentDataSetsReview] = useState<dataSetReviews | null>(null);
    const [currentPanel, setCurrentPanel] = useState<(PanelFields & { panelId?: string }) | null>(null);
    const [reviewData, setReviewData] = useState<SiteReviewFields | null>(null);
    const [owners, setOwners] = useState<SelectWithSearchDataArrayInterface[]>([]);
    const [currentOverviewGrid, setCurrentOverviewGrid] = useState('');
    const [studyMembers, setStudyMembers] = useState<Array<StudyFields_Study_members_StudyMember | null>>([]);

    const openEditReviewModal = useBoolean(false);
    const editObservationOpen = useBoolean(false);
    const addActionOpen = useBoolean(false);
    const addCompanionView = useBoolean(false);
    const addPanelDialogOpen = useBoolean(false);
    const loadingDataset = useBoolean(false);
    const isFirst = useBoolean(true);

    const [getDataSets, { data: dataSetsData, loading }] = useLazyQuery<dataSets, dataSetsVariables>(GET_DATASETS);
    const [getDataSetsReview, { data: dataSetReviewsData, loading: loadingSetsReviews, error: errorSetsReviews }] =
        useLazyQuery<dataSetReviews, dataSetReviewsVariables>(GET_DATASET_REVIEWS);
    const [getReportData, { data: reportData, loading: loadingReport }] = useLazyQuery(GET_CM_REVIEW_REPORT_DATA);

    // Apollo shares cache for all queries, in any page or component, that use the same underlying graphql query and result object
    //  Here we are querying the study with a specific timestamp based on the dataset date,
    //  and this updates the cache with potentially an older version of the study
    //  This then updates the navbar component with this older version of the study.
    //  Among other things this then caused the modules assigned to the study to change to an older version
    //  and hence hide the new central monitoring section in the page nav. It subsequently updated with a further rerender, but still not great.
    // Switch policy to 'no-cache'. This will be slower because it needs to fetch every time this query is called by legacy dashboards,
    // but it will stop updating the shared cache with an out-of-date study,
    // and it will also stop us incorrectly getting the cached info for the current study here
    const [loadStudyData, { loading: loadingStudy, data: studyData, error: errorDataStudy }] =
        useLazyQuery<getRestrictedStudy>(GET_RESTRICTED_STUDY, {
            fetchPolicy: 'no-cache',
        });
    const [loadStudyMembersData, { loading: loadingStudyMembersData, data: studyMembersData }] =
        useLazyQuery<getStudyMembers>(GET_STUDY_MEMBERS);

    // The same as the above comment about the Apollo Cache.
    // The Manage Observation page uses the same panel entities and can result in the cache updating
    // which in turn causes other components listening to the section of the cache to re-render.
    // In this case the ManageObservation component is rendered here, and by other components/pages
    // So I opted to make the query here (and not in ManageObservation) to not use the cache
    // as other components that use ManageObservation do not suffer from this problem.
    const { data: extPanels, loading: loadingPanels } = useQuery(GET_PANELS, {
        variables: {
            organisationId: user?.organisationId,
            studyId: study?.id ?? Constants.ObjectId.Empty,
        },
        fetchPolicy: 'no-cache',
    });
    const [loadFullSite, { data: fullSiteData, loading: loadingFullSite }] = useLazyQuery<getSite, getSiteVariables>(
        GET_SITE,
        {
            fetchPolicy: 'no-cache',
        },
    );

    const selectedDate = state?.selectedDatasetDate;

    useEffect(() => {
        const config = state?.scoringData?.config ?? state?.studyData?.config;
        if (config) {
            setCurrentDashboardsData(config.dashboards);
            setPanelsData(getActivePanels(config.panels));
        } else {
            setCurrentDashboardsData(null);
            setPanelsData(getActivePanels([]));
        }
    }, [state?.scoringData, state?.studyData]);

    useEffect(() => {
        setCurrentDataSets({ ...dataSetsData });
    }, [dataSetsData?.dataSets]);

    useEffect(() => {
        setCurrentDataSetsReview({ ...dataSetReviewsData });
    }, [dataSetReviewsData]);

    useEffect(() => {
        isFirst.setTrue();
        const studyId = state?.selectedStudy?.id as string;
        if (studyId) {
            // Always load the study data, as if the id hasn't changed, then the selectedDate has changed
            loadStudyData({
                variables: {
                    id: studyId,
                    ...(selectedDate?.created ? { timestamp: selectedDate.created } : {}),
                },
            });
            if (studyId !== study?.id) {
                // NOTE: We need this Additional request to get all study members, because "loadStudyData" only returns members that were created before the provided timestamp
                loadStudyMembersData({
                    variables: {
                        studyId,
                    },
                });
            }
        } else {
            setStudy(null);
            setCurrentDashboardsData(null);
        }
    }, [state?.selectedStudy?.id, selectedDate]);

    useEffect(() => {
        if (!loadingStudyMembersData && studyMembersData) {
            setStudyMembers(studyMembersData.studyMembers ?? []);
        }
    }, [studyMembersData, loadingStudyMembersData]);

    useEffect(() => {
        if (state?.studyData) {
            const config = state?.scoringData?.config;
            const member = state?.selectedStudy?.members?.find((m) => m?.userId === user?.id);

            const newSites: DashboardSite[] = state.studyData.sites
                .filter(notEmpty)
                .filter(
                    (studyDataSite) =>
                        !config?.sitesStatusesToExclude.some(
                            (status) => status === studyDataSite.Fields['Site.status'],
                        ),
                )
                .map((item: any) => {
                    const siteId = item.Fields['Site.id'];

                    return {
                        id: siteId,
                        name: item.Fields['Site.name'],
                        status: item.Fields['Site.status'],
                        default: siteId === member?.defaultSiteId,
                        favourite: member?.favouriteSiteIds?.includes(siteId) ?? false,
                    };
                });

            const sortedSites = orderBy(newSites, [
                { field: 'default', dir: 'desc' },
                { field: 'favourite', dir: 'desc' },
                { field: 'name', dir: 'asc' },
            ]);

            setSites(newSites);
            setDropdownSites(sortedSites);

            setSelectedSite(sortedSites.find((site) => site.id === siteIdFromLocationState) ?? sortedSites[0] ?? null);
        }
    }, [state?.studyData, state?.scoringData, type]);

    useEffect(() => {
        if (selectedSite?.id) {
            loadFullSite({
                variables: {
                    id: selectedSite.id as string,
                    // created is set when calculate fields is run for the dataset, and this is when the site is created for that dataset
                    timestamp: selectedDate?.created,
                },
            });
        }
    }, [selectedSite?.id]);

    useEffect(() => {
        if (studyData?.study) {
            if (studyData.study.id !== study?.id) setStudy(studyData.study);
        }
    }, [studyData]);

    useEffect(() => {
        if (studyMembers) {
            const newData = studyMembers.map((member) => ({
                id: member?.id ?? '',
                text: `${member?.firstName ?? ''} ${member?.lastName ?? ''}`.trim(),
                firstName: member?.firstName ?? '',
                lastName: member?.lastName ?? '',
                active: Boolean(member?.active),
                email: member?.email ?? '',
            }));

            setOwners(newData);
        }
    }, [studyMembers]);

    useEffect(() => {
        if (showDashboard) {
            handleTabChange(0);
        }
    }, [showDashboard]);

    useEffect(() => {
        if (study && selectedDate)
            getReportData({
                variables: {
                    studyId: study?.id,
                    dataSetId: selectedDate?.id,
                    onlyReviewed: false,
                },
            });
    }, [study, selectedDate, state?.selectedStudy?.id]);

    useEffect(() => {
        const datasetDatesResult = getDatasetDates(currentDataSets, currentDataSetsReview, user?.systemAdminLevel);

        setDatasetDates(datasetDatesResult || null);
    }, [currentDataSets, currentDataSetsReview, state?.selectedStudy?.id]);

    useEffect(() => {
        if (study) {
            getDataSets({ variables: { studyId: study?.id } });
            getDataSetsReview({ variables: { studyId: study?.id, excludeReportReviews: true } });
        } else {
            setCurrentDataSets(null);
            setCurrentDataSetsReview(null);
        }
    }, [study, state?.selectedStudy?.id]);

    const onOpenCompanionView = (item: PanelFields) => {
        setCurrentPanel(item);
        addCompanionView.setTrue();
    };

    const onOpenAddPanelDialog = (panel: PanelFields) => {
        setCurrentPanel(panel);
        addPanelDialogOpen.setTrue();
    };

    const onCloseAddPanelDialog = () => {
        if (!addCompanionView.value) {
            setCurrentPanel(null);
        }
        addPanelDialogOpen.setFalse();
    };

    if (
        loadingSetsReviews ||
        loading ||
        loadingDataset.value ||
        loadingReport ||
        loadingStudy ||
        loadingStudyMembersData ||
        loadingPanels ||
        isLoadingContext ||
        loadingFullSite
    )
        return <Loading float />;

    if (errorSetsReviews || errorDataStudy)
        return <Error error={errorSetsReviews?.message || errorDataStudy?.message} />;

    if (!canView) return <h1>{t('unauthorised')}</h1>;

    const config = state?.scoringData?.config ?? state?.studyData?.config;

    const selectedDateIsValid = () =>
        ((selectedDate?.active || (user?.systemAdminLevel ?? 0) > 0) && selectedDate?.isDataset) || selectedDate?.isCMR;

    const showDashboardPanelList = () =>
        panelsData && currentDashboardsData && selectedDate?.id && selectedDateIsValid();

    const fullSite = fullSiteData?.site;
    const siteId = (selectedSite?.id ?? '') as string;

    return (
        <>
            {addPanelDialogOpen.value && (
                <AddPanelDialog
                    currentPanel={currentPanel}
                    open={addPanelDialogOpen.value}
                    studyId={studyData?.study?.id}
                    onClose={onCloseAddPanelDialog}
                    panels={extPanels?.panels || panelsData}
                    canSave={canSavePanelSetup}
                    dashboardType={type}
                    siteId={type === DashboardType.SITE ? siteId : undefined}
                />
            )}
            {addCompanionView.value && (
                <ControlObservation
                    studyId={study.id}
                    siteId={type === DashboardType.SITE ? siteId : undefined}
                    datasetDate={selectedDate}
                >
                    {(handleDeleteObs: Function, handleRestoreObs: Function) => (
                        <CompanionView
                            open={addCompanionView.value}
                            datasetDates={[...(dataSetReviewsData?.dataSetReviews ?? [])]}
                            dataTables={config?.dataTables}
                            onClose={() => {
                                addCompanionView.setFalse();
                                setCurrentPanel(null);
                            }}
                            currentPanel={currentPanel}
                            sitesStatusesToExclude={config?.sitesStatusesToExclude ?? []}
                            panels={panelsData}
                            study={studyData?.study}
                            owners={owners}
                            studyData={state?.studyData}
                            selectedSite={fullSite}
                            datasetDate={selectedDate}
                            onOpenObservationsModal={(observation?: CentralMonitoringObservationAndActionFields) => {
                                if (observation) setCurrentObservation(observation);
                                editObservationOpen.setTrue();
                            }}
                            onOpenActionModal={(
                                action?: CentralMonitoringActionFields,
                                observation?: CentralMonitoringObservationAndActionFields,
                            ) => {
                                if (action) setCurrentAction(action);
                                if (observation) setCurrentObservation(observation);
                                addActionOpen.setTrue();
                            }}
                            type={type}
                            canEdit={canEdit}
                            onOpenAddPanelDialog={onOpenAddPanelDialog}
                            handleDeleteObs={handleDeleteObs}
                            handleRestoreObs={handleRestoreObs}
                        />
                    )}
                </ControlObservation>
            )}
            <AddAction
                study={study}
                owners={owners}
                currentAction={currentAction}
                open={addActionOpen.value}
                observationId={currentObservation?.id}
                siteInfo={type === DashboardType.SITE ? fullSite : null}
                dataset={selectedDate}
                canEdit={canEdit}
                onClose={(action: string | null) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'assign')
                            NotificationService.showNotification(
                                t('ra:successfulCreateActionAssignedToObservation', {
                                    id: currentObservation?.recordId,
                                }),
                            );
                        if (action === 'edit')
                            NotificationService.showNotification(
                                `${t('subject:action')} ${currentAction?.recordId} ${t('ra:successfulUpdated')}`,
                            );
                        addActionOpen.setFalse();
                        setCurrentAction(null);
                        setCurrentObservation(null);
                    }
                }}
            />
            <ManageObservation
                study={study}
                open={editObservationOpen.value}
                onClose={(action) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'add')
                            NotificationService.showNotification(t('cm:observationSuccessfulCreated'));
                        if (action === 'edit')
                            NotificationService.showNotification(
                                `${t('ra:observation')} ${currentObservation?.recordId} ${t('ra:successfulUpdated')}`,
                            );
                        setCurrentObservation(null);
                        editObservationOpen.setFalse();
                    }
                }}
                currentObservation={currentObservation}
                panels={getDashboardPanels(currentDashboardsData, panelsData)}
                owners={owners}
                sites={sites}
                siteInfo={type === DashboardType.SITE ? fullSite : null}
                type={
                    currentObservation?.type ??
                    getObservationTypeFromDashboard(
                        addCompanionView.value,
                        type === DashboardType.SITE,
                        currentOverviewGrid,
                    )
                }
                dataset={selectedDate}
                panelId={currentPanel?.panelId || currentObservation?.panelId || null}
                canEdit={canEdit}
                datasetDates={datasetDates}
            />
            <EditReview
                open={openEditReviewModal.value}
                onClose={(action: string) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'success')
                            NotificationService.showNotification(
                                t('cm:reviewSuccessfulUpdated', { id: reviewData?.recordId }),
                            );
                        setReviewData(null);
                        openEditReviewModal.setFalse();
                    }
                }}
                isReviewDatesReadOnly={type === DashboardType.SITE}
                reviewData={
                    type === DashboardType.SITE ? reviewData : reportData?.centralMonitoringReviewData?.studyReview
                }
                studyReviewInfo={
                    type === DashboardType.SITE ? reportData?.centralMonitoringReviewData?.studyReview : null
                }
                siteId={type === DashboardType.SITE ? siteId : null}
                dataSetId={selectedDate?.id}
                studyId={study?.id}
                canEdit={canEdit}
            />
            <div className="study-dashboard-panel-tabs">
                <Tabs
                    className="tab-dashboard-item"
                    value={active}
                    onChange={(e: any, value: any) => handleTabChange(value)}
                >
                    <Tab
                        className="tab-dashboard-label"
                        label={type === DashboardType.SITE ? t('cm:siteDashboard') : t('cm:studyDashboard')}
                        data-testid="study-dashboard-tab"
                    />
                    <Tab
                        className="tab-dashboard-label"
                        label={type === DashboardType.SITE ? t('cm:siteSummary') : t('cm:studySummary')}
                        data-testid="study-summary-tab"
                    />
                    <Tab className="tab-dashboard-label" label={t('cm:datasetNotes')} data-testid="dataset-notes-tab" />
                </Tabs>
                {type === DashboardType.SITE && (
                    <SiteDropdown
                        data={dropdownSites}
                        value={selectedSite}
                        onChange={(event) => {
                            setSelectedSite(event.target.value as DashboardSite);
                        }}
                    />
                )}
            </div>
            {!addCompanionView.value ? (
                <>
                    {active === 0 ? (
                        <>
                            {!!selectedDate && !selectedDate?.id && (
                                <span className="dashboard-hint">
                                    <b>
                                        New or modified panels will not show updated data on this page until data
                                        processing has been re-run.
                                    </b>
                                </span>
                            )}
                            {showDashboardPanelList() ? (
                                <DashboardPanelList
                                    selectedDate={selectedDate?.dataCutDate}
                                    allFullPanels={panelsData}
                                    dashboards={currentDashboardsData}
                                    type={type}
                                    onOpenCompanionView={onOpenCompanionView}
                                    onOpenAddPanelDialog={onOpenAddPanelDialog}
                                    canEdit={canEdit}
                                    sitesStatusesToExclude={config?.sitesStatusesToExclude ?? []}
                                    siteId={siteId}
                                />
                            ) : (
                                <h1>No data available</h1>
                            )}
                        </>
                    ) : (
                        <>
                            {active === 1 ? (
                                type === DashboardType.SITE ? (
                                    <ControlObservation studyId={study?.id} siteId={siteId} datasetDate={selectedDate}>
                                        {(handleDeleteObs: Function, handleRestoreObs: Function) => (
                                            <SiteDashboardSummaryV2
                                                study={studyData?.study}
                                                studyData={state?.studyData}
                                                config={config}
                                                owners={owners}
                                                selectedSite={fullSite}
                                                scoringData={state?.scoringData}
                                                canEdit={canEdit}
                                                panels={panelsData}
                                                dataSets={currentDataSets}
                                                datasetDate={selectedDate}
                                                onOpenEditReview={(review: SiteReviewFields) => {
                                                    if (review) setReviewData(review);
                                                    openEditReviewModal.setTrue();
                                                }}
                                                show={active === 1 && type === DashboardType.SITE}
                                                onOpenObservationsModal={(
                                                    observation?: CentralMonitoringObservationAndActionFields,
                                                ) => {
                                                    if (observation) setCurrentObservation(observation);
                                                    editObservationOpen.setTrue();
                                                }}
                                                onOpenActionModal={(
                                                    action?: CentralMonitoringActionFields,
                                                    observation?: CentralMonitoringObservationAndActionFields,
                                                ) => {
                                                    if (action) setCurrentAction(action);
                                                    if (observation) setCurrentObservation(observation);
                                                    addActionOpen.setTrue();
                                                }}
                                                studyReviewInfo={reportData?.centralMonitoringReviewData?.studyReview}
                                                datasetDates={[...(dataSetReviewsData?.dataSetReviews ?? [])]}
                                                studySites={reportData?.centralMonitoringReviewData?.studySites}
                                                handleDeleteObs={handleDeleteObs}
                                                handleRestoreObs={handleRestoreObs}
                                            />
                                        )}
                                    </ControlObservation>
                                ) : (
                                    <StudyDashboardSummary
                                        study={studyData?.study}
                                        owners={owners}
                                        config={config}
                                        sitesStatusesToExclude={config?.sitesStatusesToExclude ?? []}
                                        studyData={state?.studyData}
                                        siteScores={state?.scoringData?.siteScores}
                                        canEdit={canEdit}
                                        datasetDate={selectedDate}
                                        show={active === 1 && type === DashboardType.STUDY}
                                        reportData={reportData}
                                        datasetDates={datasetDates}
                                        dataSets={[...(dataSetsData?.dataSets || [])]}
                                        dataSetReviews={[...(dataSetReviewsData?.dataSetReviews || [])]}
                                        onOpenEditReview={() => {
                                            if (!selectedDate?.isDataset) return;

                                            openEditReviewModal.setTrue();
                                        }}
                                        onOpenObservationsModal={(
                                            observation?: CentralMonitoringObservationAndActionFields,
                                        ) => {
                                            if (observation) setCurrentObservation(observation);
                                            editObservationOpen.setTrue();
                                        }}
                                        onOpenActionModal={(
                                            action?: CentralMonitoringActionFields,
                                            observation?: CentralMonitoringObservationAndActionFields,
                                        ) => {
                                            if (action) setCurrentAction(action);
                                            if (observation) setCurrentObservation(observation);
                                            addActionOpen.setTrue();
                                        }}
                                        currentGrid={currentOverviewGrid}
                                        setCurrentGrid={setCurrentOverviewGrid}
                                        activeTab={activeTab}
                                        setActiveTab={setActiveTab}
                                    />
                                )
                            ) : selectedDate && selectedDate.notes ? (
                                <StudyDatasetNotes
                                    notes={selectedDate.notes}
                                    dataSetNotesTitle={t('cm:datasetNotesTitle')}
                                />
                            ) : (
                                <h1>No data available</h1>
                            )}
                        </>
                    )}
                </>
            ) : null}
        </>
    );
};

export default LegacyDashboards;
